import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { usePagePermission } from "../../Hook";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Download, SlidersHorizontal, RotateCcw, ReceiptText } from "lucide-react";
import { Spin, DatePicker, Select, Drawer, Space, Button as AntdButton } from "antd";
import { FetchSalesPersons_api, FetchDealers_api } from "../../Api";
import { SERVER_API } from "../../Constant";
import axios from "axios";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";

const { RangePicker } = DatePicker;

const toNumber = (value: any): number =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: "LKR",
    minimumFractionDigits: 2,
  }).format(value);

export const SalesInvoiceBreakdown = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    "sales_invoice_breakdown" as any,
    ["admin", "manager"],
  );

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vehicleType, setVehicleType] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [salesPersonId, setSalesPersonId] = useState<number | undefined>(undefined);
  const [dealerId, setDealerId] = useState<number | undefined>(undefined);
  const [hasApplied, setHasApplied] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<any>(null);
  const [draftVehicleType, setDraftVehicleType] = useState<string>("");
  const [draftPaymentStatus, setDraftPaymentStatus] = useState<string>("");
  const [draftSalesPersonId, setDraftSalesPersonId] = useState<number | undefined>(undefined);
  const [draftDealerId, setDraftDealerId] = useState<number | undefined>(undefined);

  const [salesPersons, setSalesPersons] = useState<any[]>([]);
  const [dealers, setDealers] = useState<any[]>([]);

  useEffect(() => {
    if (hasPermission) {
      FetchSalesPersons_api(1, 1000, "").then((response: any) => {
        if (response && response.isDataSet) {
          setSalesPersons(response.data || []);
        }
      });
      FetchDealers_api(1, 1000, "").then((response: any) => {
        if (response && response.dealerData) {
          setDealers(response.dealerData);
        }
      });
    }
  }, [hasPermission]);

  const fetchReport = async (
    start: string,
    end: string,
    vType: string = "",
    pStatus: string = "",
    spId?: number,
    dlrId?: number,
  ) => {
    if (!start || !end) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVER_API}/Reports/fetch_sales_invoice_breakdown_report.php`,
        {
          params: {
            startDate: start,
            endDate: end,
            vehicleType: vType,
            paymentStatus: pStatus,
            salesPersonId: spId,
            dealerId: dlrId,
          },
        },
      );
      const res = response.data;
      if (res.success) {
        setReportData(res.data || []);
        setTotals(res);
      } else {
        alert(res.msg || "Failed to fetch report");
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftRange(startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null);
    setDraftVehicleType(vehicleType);
    setDraftPaymentStatus(paymentStatus);
    setDraftSalesPersonId(salesPersonId);
    setDraftDealerId(dealerId);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    const s = draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "";
    const e = draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "";
    setStartDate(s);
    setEndDate(e);
    setVehicleType(draftVehicleType);
    setPaymentStatus(draftPaymentStatus);
    setSalesPersonId(draftSalesPersonId);
    setDealerId(draftDealerId);
    setHasApplied(true);
    setDrawerOpen(false);
    fetchReport(s, e, draftVehicleType, draftPaymentStatus, draftSalesPersonId, draftDealerId);
  };

  const resetFilters = () => {
    setDraftRange(null);
    setDraftVehicleType("");
    setDraftPaymentStatus("");
    setDraftSalesPersonId(undefined);
    setDraftDealerId(undefined);
    setStartDate("");
    setEndDate("");
    setVehicleType("");
    setPaymentStatus("");
    setSalesPersonId(undefined);
    setDealerId(undefined);
    setReportData([]);
    setTotals({});
    setHasApplied(false);
    setDrawerOpen(false);
  };

  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
  const activeSalesPersonName =
    salesPersons.find((sp) => sp.id === salesPersonId)?.name || "";
  const activeDealerName = dealers.find((d) => d.id === dealerId)?.name || "";
  const filterSummary = [
    periodLabel,
    activeSalesPersonName,
    activeDealerName,
    vehicleType ? `Type: ${vehicleType}` : "",
    paymentStatus ? `Status: ${paymentStatus}` : "",
  ].filter(Boolean).join(" | ");

  const handleDownloadExcel = () => {
    const headers = [
      "Date",
      "Invoice #",
      "Customer",
      "Mobile",
      "Vehicle",
      "Type",
      "Engine",
      "Chassis",
      "Sales Person",
      "Dealer",
      "Invoice Total",
      "Direct Paid",
      "DPMC Paid",
      "Dealer Payment",
      "Company Paid",
      "Total Paid",
      "Outstanding",
    ];
    const data = reportData.map((row) => [
      row.date ? String(row.date).split(" ")[0] : "N/A",
      row.inquiry_number || "N/A",
      row.customer_name || "N/A",
      row.customer_mobile || "N/A",
      row.vehicle_model || "N/A",
      row.vehicle_type || "N/A",
      row.engine_number || "N/A",
      row.chassis_number || "N/A",
      row.sales_person_name || "N/A",
      row.dealer_name || "N/A",
      toNumber(row.total_amount),
      toNumber(row.direct_paid),
      toNumber(row.remittance_paid),
      toNumber(row.dealer_paid),
      toNumber(row.company_paid),
      toNumber(row.paid_amount),
      toNumber(row.outstanding_amount),
    ]);
    downloadExcel(
      `Sales Invoice Breakdown${filterSummary ? ` (${filterSummary})` : ""}`,
      headers,
      data,
      `Sales_Invoice_Breakdown_${startDate}_${endDate}`,
      formatCurrency(toNumber(totals.totalPaid)),
      formatCurrency(toNumber(totals.totalOutstanding)),
      formatCurrency(toNumber(totals.totalInvoiced)),
      `${reportData.length} Invoices`,
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Sales Invoice Breakdown Report</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <ReceiptText className="w-4 h-4 text-emerald-600" />
          <span>Filters: </span>
          <span className="font-medium text-gray-700">
            {filterSummary || "None applied — open Filters to pick a period"}
          </span>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL INVOICED</p>
                <p className="text-2xl font-bold">{formatCurrency(toNumber(totals.totalInvoiced))}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL PAID</p>
                <p className="text-2xl font-bold">{formatCurrency(toNumber(totals.totalPaid))}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">OUTSTANDING</p>
                <p className="text-2xl font-bold">{formatCurrency(toNumber(totals.totalOutstanding))}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">INVOICES</p>
                <p className="text-2xl font-bold">{reportData.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Invoice #</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Customer</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Vehicle</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Sales Person</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Invoice Total</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Payment Breakdown</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Total Paid</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Outstanding</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow
                        key={`${row.inquiry_number}-${index}`}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-4 py-3">
                          {row.date ? String(row.date).split(" ")[0] : "N/A"}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {row.inquiry_number || "N/A"}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700">{row.customer_name || "N/A"}</span>
                            <span className="text-xs text-gray-400">{row.customer_mobile || ""}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700">
                              {row.vehicle_model} <span className="text-gray-400">({row.vehicle_type})</span>
                            </span>
                            <span className="text-[10px] text-gray-400 font-mono">
                              {row.engine_number} / {row.chassis_number}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-700">{row.sales_person_name || "N/A"}</span>
                            <span className="text-xs text-gray-400">{row.dealer_name || ""}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">
                          {formatCurrency(toNumber(row.total_amount))}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {toNumber(row.direct_paid) > 0 && (
                              <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px]">
                                Direct {formatCurrency(toNumber(row.direct_paid))}
                              </span>
                            )}
                            {toNumber(row.remittance_paid) > 0 && (
                              <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100 text-[10px]">
                                DPMC {formatCurrency(toNumber(row.remittance_paid))}
                              </span>
                            )}
                            {toNumber(row.dealer_paid) > 0 && (
                              <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 text-[10px]">
                                Dealer {formatCurrency(toNumber(row.dealer_paid))}
                              </span>
                            )}
                            {toNumber(row.company_paid) > 0 && (
                              <span className="px-2 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-100 text-[10px]">
                                Company {formatCurrency(toNumber(row.company_paid))}
                              </span>
                            )}
                            {toNumber(row.paid_amount) === 0 && (
                              <span className="px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-100 text-[10px]">
                                No payment recorded
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">
                          {formatCurrency(toNumber(row.paid_amount))}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <span
                            className={
                              toNumber(row.outstanding_amount) > 0
                                ? "font-medium text-red-600"
                                : "font-medium text-emerald-600"
                            }
                          >
                            {formatCurrency(toNumber(row.outstanding_amount))}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && reportData.length === 0 && hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No sales invoices recorded for the applied period.</p>
          </div>
        </div>
      )}
      {!loading && reportData.length === 0 && !hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Open Filters and pick a period to view the sales invoice breakdown.</p>
          </div>
        </div>
      )}

      <Drawer
        title="Sales Invoice Breakdown Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Vehicle Type
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Types"
              value={draftVehicleType || undefined}
              onChange={(val: any) => setDraftVehicleType(val || "")}
              allowClear
              options={[
                { value: "Bike", label: "Bike" },
                { value: "Three Wheel", label: "Three Wheel" },
              ]}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Payment Status
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Statuses"
              value={draftPaymentStatus || undefined}
              onChange={(val: any) => setDraftPaymentStatus(val || "")}
              allowClear
              options={[
                { value: "Paid", label: "Paid" },
                { value: "pending", label: "Pending" },
              ]}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Sales Person
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Sales Persons"
              value={draftSalesPersonId}
              onChange={(val: any) => setDraftSalesPersonId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
              options={salesPersons.map((sp) => ({ value: sp.id, label: sp.name }))}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Dealer
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Dealers"
              value={draftDealerId}
              onChange={(val: any) => setDraftDealerId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
              options={dealers.map((d) => ({ value: d.id, label: d.name }))}
            />
          </div>
        </Space>
      </Drawer>
    </div>
  );
};